import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useUserProfile, useUpdateUserPreferences } from '../hooks/useUserProfile';

const PreferencesSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const { data: userProfile, isLoading: profileLoading } = useUserProfile();
  const updatePreferences = useUpdateUserPreferences();
  
  const [darkMode, setDarkMode] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [soundsEnabled, setSoundsEnabled] = useState(true);

  // Initialize state from user preferences when data is loaded
  useEffect(() => {
    if (userProfile?.preferences) {
      setDarkMode(userProfile.preferences.theme === 'dark');
      setNotificationsEnabled(userProfile.preferences.notifications || true);
    }
  }, [userProfile]);

  // Handle preference changes
  const handleToggleDarkMode = async () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    
    try {
      await updatePreferences.mutateAsync({
        theme: newDarkMode ? 'dark' : 'light'
      });
    } catch (error) {
      console.error('Failed to update theme preference:', error);
      // Revert UI if update fails
      setDarkMode(!newDarkMode);
    }
  };

  const handleToggleNotifications = async () => {
    const newNotificationsEnabled = !notificationsEnabled;
    setNotificationsEnabled(newNotificationsEnabled);
    
    try {
      await updatePreferences.mutateAsync({
        notifications: newNotificationsEnabled
      });
      
      // If notifications are disabled, also disable sounds
      if (!newNotificationsEnabled && soundsEnabled) {
        setSoundsEnabled(false);
      }
    } catch (error) {
      console.error('Failed to update notification preference:', error);
      // Revert UI if update fails
      setNotificationsEnabled(!newNotificationsEnabled);
    }
  };

  const handleToggleSounds = async () => {
    // Only allow toggling sounds if notifications are enabled
    if (!notificationsEnabled) return;
    
    const newSoundsEnabled = !soundsEnabled;
    setSoundsEnabled(newSoundsEnabled);
    
    // Note: This assumes there's a sounds preference field in the UserProfile type
    // If not, this would need to be handled differently
  };

  if (profileLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading preference data...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Preferences</h3>
        <p className="text-gray-500">Customize your app experience</p>
      </div>

      <div className="space-y-6">
        {/* Appearance */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Appearance
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Customize how the app looks
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between">
              <span className="flex-grow flex flex-col">
                <span className="text-sm font-medium text-gray-900">Dark Mode</span>
                <span className="text-sm text-gray-500">
                  Use dark theme for the application
                </span>
              </span>
              <button 
                onClick={handleToggleDarkMode}
                className={`${
                  darkMode ? 'bg-blue-600' : 'bg-gray-200'
                } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                role="switch"
                aria-checked={darkMode}
              >
                <span
                  className={`${
                    darkMode ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Notifications
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Control how the app notifies you
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="flex-grow flex flex-col">
                <span className="text-sm font-medium text-gray-900">Enable Notifications</span>
                <span className="text-sm text-gray-500">
                  Receive notifications for important events
                </span>
              </span>
              <button 
                onClick={handleToggleNotifications}
                className={`${
                  notificationsEnabled ? 'bg-blue-600' : 'bg-gray-200'
                } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                role="switch"
                aria-checked={notificationsEnabled}
              >
                <span
                  className={`${
                    notificationsEnabled ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex-grow flex flex-col">
                <span className="text-sm font-medium text-gray-900">Sounds</span>
                <span className="text-sm text-gray-500">
                  Play sounds for notifications
                </span>
              </span>
              <button 
                onClick={handleToggleSounds}
                className={`${
                  soundsEnabled ? 'bg-blue-600' : 'bg-gray-200'
                } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                role="switch"
                aria-checked={soundsEnabled}
                disabled={!notificationsEnabled}
              >
                <span
                  className={`${
                    soundsEnabled ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 rounded-full ${notificationsEnabled ? 'bg-white' : 'bg-gray-100'} shadow transform ring-0 transition ease-in-out duration-200`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Language Preferences - Coming soon */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Language
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Change the app language
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <div className="text-sm">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Coming Soon
              </span>
              <p className="mt-2 text-gray-500">
                Language preferences will be available in a future update.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreferencesSettingsPage; 